import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BookMarked, Plus, Search } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { Skeleton, SkeletonRegion } from '@/components/Skeleton';
import { cn } from '@/lib/cn';
import { formatarDistancia } from '@/lib/format';
import { Modal } from '@/features/tickets/Modal';
import { listarChamados } from '@/features/tickets/tickets.api';
import { AvisoDeFalha } from '@/features/workflow/componentes';
import { classificarFalha, type Falha } from '@/features/workflow/recusa';
import { Campo, CLASSE_DE_CAMPO } from '@/features/changes/componentes';
import {
  chavesDeKedb,
  criarErroConhecido,
  listarErrosConhecidos,
} from '@/features/kedb/kedb.api';
import { EtiquetaDeSituacaoDeErro, Recorrencia } from '@/features/kedb/componentes';
import { LinkDoErroConhecido, useBuscaDeErros } from '@/features/kedb/navegacao';
import {
  ROTULO_DE_SITUACAO_DE_ERRO,
  SITUACOES_DE_ERRO,
  type SituacaoDeErro,
} from '@/features/kedb/tipos';

const ESPERA_DA_BUSCA = 300;

/**
 * A Base de Erros Conhecidos.
 *
 * ## Uma nota honesta sobre esta busca
 *
 * O campo aqui é `ILIKE` sobre título, sintomas e código — é a busca da
 * estante, para achar o registro que se sabe que existe. **Não** é a busca por
 * similaridade: aquela é a do atendimento, roda no índice de texto completo em
 * português somado à identidade do item de configuração e do serviço, e aparece
 * sozinha dentro do chamado. Chamar as duas pelo mesmo nome faria a segunda
 * parecer supérflua, quando é ela que dá o valor da KEDB.
 */
export function ErrosConhecidosPage() {
  const { termo, definir } = useBuscaDeErros();
  const [texto, setTexto] = useState(termo);
  const [status, setStatus] = useState<SituacaoDeErro | 'TODOS'>('ACTIVE');
  const [criando, setCriando] = useState(false);

  /**
   * Digitar espera antes de navegar: sem isso, "impressora" seriam dez
   * substituições no histórico e dez consultas, nove já obsoletas.
   *
   * `definir` fica numa referência e **fora** das dependências de propósito: o
   * hook devolve uma função nova a cada renderização, e listá-la faria o efeito
   * se reinscrever — reiniciando o relógio — em toda renderização. Uma espera
   * que reinicia sozinha é uma espera que pode nunca vencer.
   */
  const definirAgora = useRef(definir);
  definirAgora.current = definir;

  useEffect(() => {
    const relogio = setTimeout(() => {
      if (texto.trim() !== termo) definirAgora.current(texto.trim());
    }, ESPERA_DA_BUSCA);
    return () => clearTimeout(relogio);
  }, [texto, termo]);

  const consulta = useQuery({
    queryKey: chavesDeKedb.lista(status, termo),
    queryFn: () =>
      listarErrosConhecidos({
        ...(status === 'TODOS' ? {} : { status }),
        ...(termo ? { q: termo } : {}),
      }),
  });

  return (
    <div className="mx-auto max-w-[1400px] space-y-3">
      <header className="flex items-center justify-between gap-4 border-b border-border pb-3">
        <h1 className="text-base font-semibold">Erros conhecidos</h1>
        <Button
          size="sm"
          onClick={() => setCriando(true)}
          icon={<Plus className="h-4 w-4" aria-hidden />}
        >
          Publicar erro conhecido
        </Button>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1 space-y-1.5">
          <label htmlFor="busca-kedb" className="text-sm font-medium">
            Buscar
          </label>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
              aria-hidden
            />
            <input
              id="busca-kedb"
              value={texto}
              onChange={(evento) => setTexto(evento.target.value)}
              placeholder="Código, título ou sintoma"
              className={cn(CLASSE_DE_CAMPO, 'pl-9')}
            />
          </div>
        </div>

        <Campo rotulo="Situação" htmlFor="filtro-kedb">
          <select
            id="filtro-kedb"
            value={status}
            onChange={(evento) => setStatus(evento.target.value as SituacaoDeErro | 'TODOS')}
            className={cn(CLASSE_DE_CAMPO, 'w-auto')}
          >
            <option value="TODOS">Todos</option>
            {SITUACOES_DE_ERRO.map((valor) => (
              <option key={valor} value={valor}>
                {ROTULO_DE_SITUACAO_DE_ERRO[valor]}
              </option>
            ))}
          </select>
        </Campo>
      </div>

      {consulta.isPending ? (
        <SkeletonRegion label="Carregando erros conhecidos" className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </SkeletonRegion>
      ) : null}

      {consulta.isError ? (
        <AvisoDeFalha
          falha={classificarFalha(consulta.error)}
          aoRecarregar={() => void consulta.refetch()}
        />
      ) : null}

      {consulta.data?.length === 0 ? (
        <EmptyState
          icon={<BookMarked className="h-7 w-7" aria-hidden />}
          title={termo ? 'Nada encontrado com este termo' : 'Nenhum erro conhecido publicado'}
          description={
            termo
              ? 'A busca desta tela é literal, sobre código, título e sintomas. A busca por similaridade — com stemming e identidade de item de configuração — é a que roda dentro do chamado.'
              : 'Um erro conhecido nasce de um problema com causa identificada, e traz sempre a solução de contorno: sem ela, é um problema em aberto com outro nome.'
          }
        />
      ) : null}

      {consulta.data && consulta.data.length > 0 ? (
        <Card>
          <CardBody className="p-0">
            <ul className="divide-y divide-border">
              {consulta.data.map((erro) => (
                <li key={erro.id} className="space-y-1 px-5 py-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <LinkDoErroConhecido
                      erroId={erro.id}
                      className="font-mono text-sm font-semibold text-primary hover:underline"
                    >
                      {erro.code}
                    </LinkDoErroConhecido>
                    <span className="min-w-0 flex-1 truncate font-medium">{erro.title}</span>
                    <Recorrencia contagem={erro.occurrenceCount} />
                    <EtiquetaDeSituacaoDeErro situacao={erro.status} />
                  </div>
                  <p className="line-clamp-2 text-sm text-muted-foreground">{erro.symptoms}</p>
                  <p className="text-xs text-muted-foreground">
                    Nasceu do problema {erro.problemTicket.number} · publicado{' '}
                    {formatarDistancia(erro.createdAt)}
                  </p>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      ) : null}

      {criando ? <DialogoDeErroConhecido aoFechar={() => setCriando(false)} /> : null}
    </div>
  );
}

// ── publicação ─────────────────────────────────────────────────────────────

const MINIMO_DE_TEXTO = 10;

/**
 * Publicar um erro conhecido.
 *
 * Ele **nasce de um problema** — o banco garante isso por chave composta, e não
 * só a aplicação. É o que separa "sabemos a causa" de "vamos anotando", e é por
 * isso que o primeiro campo é o problema e não o título.
 */
function DialogoDeErroConhecido({ aoFechar }: { aoFechar: () => void }) {
  const cliente = useQueryClient();
  const [problema, setProblema] = useState('');
  const [titulo, setTitulo] = useState('');
  const [sintomas, setSintomas] = useState('');
  const [contorno, setContorno] = useState('');
  const [causa, setCausa] = useState('');
  const [falha, setFalha] = useState<Falha | null>(null);

  const problemas = useQuery({
    queryKey: ['chamados', 'lista', 'problemas-para-kedb'],
    queryFn: () => listarChamados({ type: ['PROBLEM'], limit: 100 }),
  });

  const criar = useMutation({
    mutationFn: () =>
      criarErroConhecido({
        problemTicketId: problema,
        title: titulo.trim(),
        symptoms: sintomas.trim(),
        workaround: contorno.trim(),
        ...(causa.trim() ? { rootCause: causa.trim() } : {}),
      }),
    onMutate: () => setFalha(null),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: chavesDeKedb.raiz });
      aoFechar();
    },
    onError: (erro) => setFalha(classificarFalha(erro)),
  });

  const incompleto =
    problema === '' ||
    titulo.trim().length < 3 ||
    sintomas.trim().length < MINIMO_DE_TEXTO ||
    contorno.trim().length < MINIMO_DE_TEXTO;

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Publicar erro conhecido"
      descricao="O código sai do esquema de identificadores do tenant (prefixo KE). O contador de ocorrências é mantido pelo banco — nem a aplicação o escreve."
    >
      <form
        className="space-y-4 px-5 py-4"
        onSubmit={(evento) => {
          evento.preventDefault();
          if (!incompleto) criar.mutate();
        }}
      >
        <Campo
          rotulo="Problema de origem"
          htmlFor="kedb-problema"
          dica="Erro conhecido nasce de um problema. O banco recusa qualquer outro tipo de chamado — a checagem do servidor existe só para a mensagem ser em português."
        >
          <select
            id="kedb-problema"
            value={problema}
            onChange={(evento) => setProblema(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          >
            <option value="">Escolha um problema…</option>
            {(problemas.data?.items ?? []).map((chamado) => (
              <option key={chamado.id} value={chamado.id}>
                {chamado.number} — {chamado.subject}
              </option>
            ))}
          </select>
        </Campo>

        {problemas.data?.items.length === 0 ? (
          <p className="text-xs text-muted-foreground">
            Nenhum problema registrado. Abra o problema primeiro: é nele que a causa raiz mora.
          </p>
        ) : null}

        <Campo rotulo="Título" htmlFor="kedb-titulo">
          <input
            id="kedb-titulo"
            value={titulo}
            onChange={(evento) => setTitulo(evento.target.value)}
            className={CLASSE_DE_CAMPO}
            placeholder="Fila de impressão trava depois da atualização do driver"
          />
        </Campo>

        <Campo
          rotulo="Sintomas"
          htmlFor="kedb-sintomas"
          dica="Escreva com as palavras de quem abre o chamado, não com as de quem diagnostica: é por este texto que a busca do atendimento casa."
        >
          <textarea
            id="kedb-sintomas"
            rows={4}
            value={sintomas}
            onChange={(evento) => setSintomas(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo
          rotulo="Solução de contorno"
          htmlFor="kedb-contorno"
          dica="Obrigatória — é a razão de o erro conhecido existir. Sem ela, o registro só faria o operador perder tempo abrindo algo que não ajuda."
        >
          <textarea
            id="kedb-contorno"
            rows={4}
            value={contorno}
            onChange={(evento) => setContorno(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        <Campo rotulo="Causa raiz" htmlFor="kedb-causa">
          <textarea
            id="kedb-causa"
            rows={3}
            value={causa}
            onChange={(evento) => setCausa(evento.target.value)}
            className={CLASSE_DE_CAMPO}
          />
        </Campo>

        {falha ? <AvisoDeFalha falha={falha} /> : null}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="secondary" onClick={aoFechar} disabled={criar.isPending}>
            Cancelar
          </Button>
          <Button type="submit" loading={criar.isPending} disabled={incompleto}>
            Publicar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
